<template>
    <v-dialog :value="value" max-width="640" persistent scrollable @input="cerrar">
        <v-card>
            <v-card-title class="text-subtitle-1 font-weight-bold">
                <v-icon left color="primary">event</v-icon>
                {{ modelo.programacion_id ? 'Editar programación' : 'Nueva programación' }}
                <v-spacer></v-spacer>
                <v-btn icon small @click="cerrar(false)"><v-icon>close</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valido" @submit.prevent="guardar">
                    <v-row dense>
                        <v-col cols="12">
                            <v-select v-model="modelo.tema_id" :items="temasActivos" item-text="nombre" item-value="tema_id" label="Tema que se mostrará"
                                      outlined dense :rules="[v => !!v || 'Elija el tema']">
                                <template v-slot:item="{ item }">
                                    <span class="muestra" :style="{background: item.colores.primario}"></span>
                                    <span class="muestra" :style="{background: item.colores.acento}"></span>
                                    {{ item.nombre }}
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="modelo.fecha_desde" type="date" label="Desde" outlined dense
                                          :rules="[v => !!v || 'Indique la fecha de inicio']"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="modelo.fecha_hasta" type="date" label="Hasta" outlined dense
                                          :rules="[v => !!v || 'Indique la fecha de fin', v => !v || !modelo.fecha_desde || v >= modelo.fecha_desde || 'No puede ser anterior a la fecha de inicio']"/>
                        </v-col>
                        <v-col cols="12" sm="8">
                            <v-switch v-model="modelo.recurrente_anual" inset hide-details color="primary" class="mt-1"
                                      :label="modelo.recurrente_anual ? 'Se repite cada año (solo cuentan mes y día)' : 'Solo en estas fechas, una vez'"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field v-model.number="modelo.prioridad" type="number" min="0" max="100" label="Prioridad" outlined dense
                                          hint="Si dos coinciden, gana la mayor." persistent-hint
                                          :rules="[v => (v !== '' && v !== null && Number(v) >= 0 && Number(v) <= 100 && Number.isInteger(Number(v))) || 'Entero entre 0 y 100']"/>
                        </v-col>
                        <v-col cols="12">
                            <v-switch v-model="modelo.activo" inset hide-details color="success" class="mt-0"
                                      :label="modelo.activo ? 'Activa: se aplica en las fechas indicadas' : 'Inactiva: se guarda pero no se aplica'"/>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="cerrar(false)">Cancelar</v-btn>
                <v-btn color="primary" depressed :loading="guardando" @click="guardar">
                    <v-icon small left>save</v-icon> Guardar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/** Alta o edición de una programación por fechas de un tema. */
export default {
    name: "ProgramacionFormulario",
    props: {
        value: {type: Boolean, default: false},
        programacion: {type: Object, default: null},
        temas: {type: Array, default: () => []},
    },
    data() {
        return {
            valido: true,
            guardando: false,
            modelo: this.vacio(),
        };
    },
    computed: {
        temasActivos() {
            return this.temas.filter(t => t.activo || (this.programacion && this.programacion.tema_id === t.tema_id));
        },
    },
    watch: {
        value(abierto) {
            if (abierto) this.iniciar();
        },
    },
    methods: {
        vacio() {
            const hoy = new Date();
            const anio = hoy.getFullYear();
            const mes = String(hoy.getMonth() + 1).padStart(2, '0');
            return {
                programacion_id: null,
                tema_id: null,
                fecha_desde: `${anio}-${mes}-01`,
                fecha_hasta: `${anio}-${mes}-${new Date(anio, hoy.getMonth() + 1, 0).getDate()}`,
                recurrente_anual: true,
                prioridad: 10,
                activo: true,
            };
        },
        iniciar() {
            this.modelo = this.programacion
                ? {...this.vacio(), ...this.programacion}
                : this.vacio();
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        cerrar(valor) {
            if (!this.guardando) this.$emit('input', !!valor);
        },
        guardar() {
            if (!this.$refs.form.validate()) return;

            this.guardando = true;
            const datos = {
                programacion_id: this.modelo.programacion_id || null,
                tema_id: this.modelo.tema_id,
                fecha_desde: this.modelo.fecha_desde,
                fecha_hasta: this.modelo.fecha_hasta,
                recurrente_anual: this.modelo.recurrente_anual ? 1 : 0,
                prioridad: Number(this.modelo.prioridad),
                activo: this.modelo.activo ? 1 : 0,
            };

            window.axios.post('/apariencia/programaciones/guardar', datos).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.$emit('guardado', response.data.data);
                    this.$emit('input', false);
                } else {
                    this.$toast.error(response.data.message, {timeout: 10000});
                }
            })
                .catch(error => {
                    const cuerpo = error.response && error.response.data;
                    const detalle = cuerpo && cuerpo.data && typeof cuerpo.data === 'object' ? [].concat(...Object.values(cuerpo.data))[0] : null;
                    this.$toast.error(detalle || (cuerpo && cuerpo.message) || 'No se pudo guardar la programación.', {timeout: 10000});
                })
                .finally(() => {
                    this.guardando = false;
                });
        },
    },
}
</script>

<style scoped>
.muestra {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    margin-right: 4px;
    border: 1px solid rgba(0, 0, 0, .15);
}
</style>
